import { useEffect, useState } from 'react';
import { ApiService } from '../../../services/ApiService';
import type { MonitoredStrategy } from '../../../types/Alert';
import { EditMonitorModal } from './EditMonitorModal';
import { useDomainContext } from '../../../contexts/DomainContext';
import { GlassSkeleton } from '../common/GlassSkeleton';

interface Props {
    onLoadStrategy?: (strategyJson: string, name: string, monitorId: number, conditionJson: string) => void;
}

export function MonitorOverview({ onLoadStrategy }: Props) {
    const [strategies, setStrategies] = useState<MonitoredStrategy[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingStrategy, setEditingStrategy] = useState<MonitoredStrategy | null>(null);
    const [heartbeat, setHeartbeat] = useState<string | null>(null);
    const { activeDomainId } = useDomainContext();

    const fetchHeartbeat = async () => {
        try {
            const json = await ApiService.getHeartbeat();
            if (json.status) setHeartbeat(json.status);
        } catch (e) {
            console.error("Failed to fetch heartbeat", e);
        }
    };

    const fetchStrategies = async () => {
        setLoading(true);
        setError(null);
        try {
            const json = await ApiService.getMonitoredAlerts(activeDomainId || undefined);
            if (json.data) {
                setStrategies(json.data as MonitoredStrategy[]);
            } else {
                setError('No data returned from server');
            }
        } catch (e) {
            console.error('Failed to fetch monitored strategies', e);
            setError(e instanceof Error ? e.message : 'Network error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStrategies();
        fetchHeartbeat();
        const interval = setInterval(fetchHeartbeat, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [activeDomainId]);

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to stop monitoring this strategy?')) return;
        try {
            await ApiService.deleteAlert(String(id));
            setStrategies(prev => prev.filter(s => s.ID !== id));
        } catch (e) {
            alert('Delete failed: ' + e);
        }
    };

    const handleEditSuccess = (updated: MonitoredStrategy) => {
        setStrategies(prev => prev.map(s => s.ID === updated.ID ? updated : s));
    };

    if (loading && strategies.length === 0) {
        return (
            <div style={{ padding: '2rem', height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                    <GlassSkeleton width="300px" height="2rem" />
                    <GlassSkeleton width="120px" height="2rem" />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                    <GlassSkeleton width="20%" height="2.5rem" />
                    <GlassSkeleton width="35%" height="2.5rem" />
                    <GlassSkeleton width="15%" height="2.5rem" />
                    <GlassSkeleton width="10%" height="2.5rem" />
                    <GlassSkeleton width="10%" height="2.5rem" />
                    <GlassSkeleton width="10%" height="2.5rem" />
                </div>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '0.5rem' }}>
                        <GlassSkeleton width="20%" height="3rem" />
                        <GlassSkeleton width="35%" height="3rem" />
                        <GlassSkeleton width="15%" height="3rem" />
                        <GlassSkeleton width="10%" height="3rem" />
                        <GlassSkeleton width="10%" height="3rem" />
                        <GlassSkeleton width="10%" height="3rem" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div style={{ padding: '2rem', height: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>🔍 Monitored Strategies</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {heartbeat && (() => {
                        const isIso = heartbeat.includes('T');
                        const hbNormalized = (isIso ? heartbeat : heartbeat.replace(' ', 'T')) + (!heartbeat.endsWith('Z') ? 'Z' : '');
                        const hbDate = new Date(hbNormalized);
                        const isStale = (new Date().getTime() - hbDate.getTime()) > 600000;
                        return (
                            <span style={{
                                fontSize: '0.8rem',
                                color: isStale ? 'var(--monitor-heartbeat-stale-fg)' : 'var(--monitor-heartbeat-ok-fg)',
                                backgroundColor: isStale ? 'var(--monitor-heartbeat-stale-bg)' : 'var(--monitor-heartbeat-ok-bg)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid currentColor'
                            }}>
                                Runner Heartbeat: {hbDate.toLocaleTimeString()}
                            </span>
                        );
                    })()}
                    <button
                        onClick={fetchStrategies}
                        disabled={loading}
                        style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            backgroundColor: 'var(--monitor-refresh-bg)',
                            color: 'white',
                            border: 'none',
                            cursor: loading ? 'progress' : 'pointer',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                        }}
                    >
                        {loading ? 'Refreshing...' : '🔄 Refresh List'}
                    </button>
                </div>
            </div>

            {error && <div style={{ color: 'red', marginBottom: '1rem' }}>Error: {error}</div>}

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--surface-glass-border)' }}>
                        <th style={{ padding: '8px' }}>Name</th>
                        <th style={{ padding: '8px' }}>Description</th>
                        <th style={{ padding: '8px' }}>Action</th>
                        <th style={{ padding: '8px' }}>Status</th>
                        <th style={{ padding: '8px' }}>Last Run</th>
                        <th style={{ padding: '8px' }}>Manage</th>
                    </tr>
                </thead>
                <tbody>
                    {strategies.map((s) => (
                        <tr key={s.ID} style={{ borderBottom: '1px solid var(--surface-glass-border)' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{s.Name}</td>
                            <td style={{ padding: '8px' }}>
                                {s.Description || <i style={{ color: 'var(--text-muted)' }}>No description</i>}
                            </td>
                            <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                                {s.Action || <i style={{ color: 'var(--text-muted)' }}>—</i>}
                            </td>

                            <td style={{ padding: '8px' }}>
                                {(() => {
                                    if (s.Status === 1) {
                                        return (
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--accent-danger-subtle)',
                                                color: 'var(--accent-danger)',
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold'
                                            }}>
                                                🔥 TRIGGERED
                                            </span>
                                        );
                                    } else if (s.Status === 2) {
                                        return (
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--bg-app)',
                                                color: 'var(--text-muted)',
                                                border: '1px dashed var(--text-muted)',
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold'
                                            }} title="Suppressed by Relevance Rule">
                                                ⛔ SUPPRESSED
                                            </span>
                                        );
                                    } else {
                                        return (
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: 'var(--accent-success-subtle)',
                                                color: 'var(--accent-success)',
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold'
                                            }}>
                                                ✅ MONITORING
                                            </span>
                                        );
                                    }
                                })()}
                            </td>
                            <td style={{ padding: '8px', fontSize: '0.85rem' }}>
                                {s.LastRun ? (() => {
                                    const isIso = s.LastRun.includes('T');
                                    const norm = (isIso ? s.LastRun : s.LastRun.replace(' ', 'T')) + (!s.LastRun.endsWith('Z') ? 'Z' : '');
                                    return new Date(norm).toLocaleString();
                                })() : 'Never'}
                            </td>
                            <td style={{ padding: '8px' }}>
                                <div style={{ display: 'flex', gap: '0.4rem' }}>
                                    <button onClick={() => onLoadStrategy?.(s.StrategyJson, s.Name, s.ID, s.ConditionJson ?? "")} title="Load into Workspace">📥 Load</button>
                                    <button onClick={() => setEditingStrategy(s)} title="Edit strategy">✏️</button>
                                    <button onClick={() => handleDelete(s.ID)} title="Delete" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>🗑️</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {strategies.length === 0 && !loading && (
                        <tr>
                            <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                No strategies being monitored. Go to the Lab to add one.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {editingStrategy && (
                <EditMonitorModal
                    strategy={editingStrategy}
                    onClose={() => setEditingStrategy(null)}
                    onSuccess={handleEditSuccess}
                />
            )}
        </div>
    );
}

